import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../hooks/useAuth'
import { useTransactions } from '../hooks/useTransactions'
import { useAIChat } from '../hooks/useAIChat'
import { useBudgets } from '../hooks/useBudgets'
import Navigation from '../components/Navigation'
import TransactionForm from '../components/spending/TransactionForm'
import TransactionList from '../components/spending/TransactionList'
import SpendingOverview from '../components/spending/SpendingOverview'
import AIBudgetChat from '../components/ai-budget/AIBudgetChat'
import BudgetList from '../components/ai-budget/BudgetList'
import EnhancedLoadingSpinner from '../components/EnhancedLoadingSpinner'
import { Transaction, Budget } from '../types/dashboard'
import { Calculator, Sparkles, BarChart3, Plus } from 'lucide-react'

const SpendingPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth()
  const { 
    transactions, 
    loading: transactionsLoading, 
    error: transactionsError,
    addTransaction: addTransactionToDB,
    updateTransaction: updateTransactionInDB,
    deleteTransaction: deleteTransactionFromDB
  } = useTransactions()
  
  // AI Budget functionality
  const {
    messages,
    isLoading: aiLoading,
    error: aiError,
    sendMessage,
    clearError,
    currentSession,
    createNewSession,
    latestBudgetRecommendation,
    budgetMonthlyIncome,
    saveBudget
  } = useAIChat()

  // Budget management
  const {
    budgets: savedBudgets,
    loading: budgetsLoading,
    error: budgetsError,
    refreshBudgets,
    deleteBudget,
    updateBudgetStatus
  } = useBudgets()
  
  const [_budgets] = useState<Budget[]>([])
  const [showTransactionForm, setShowTransactionForm] = useState(false)
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'budget'>('overview')

  console.log('SpendingPage render:', { user, authLoading, transactions, transactionsLoading, transactionsError })

  if (authLoading || transactionsLoading) {
    console.log('SpendingPage: Still loading auth or transactions')
    return (
      <EnhancedLoadingSpinner
        title="Loading Your Spending Data"
        subtitle="Analyzing your transactions and budget information..."
        icons={[
          <Calculator className="w-8 h-8 text-primary-500" />,
          <BarChart3 className="w-8 h-8 text-primary-500" />,
          <Sparkles className="w-8 h-8 text-primary-500" />,
          <Plus className="w-8 h-8 text-primary-500" />
        ]}
        showProgressBar={true}
      />
    )
  }

  if (transactionsError) {
    console.error('SpendingPage: Transactions error:', transactionsError)
  }


  const handleAddTransaction = async (transaction: Omit<Transaction, 'id'>) => {
    console.log('SpendingPage: Handling add transaction:', transaction)
    try {
      await addTransactionToDB(transaction)
      console.log('SpendingPage: Transaction added successfully')
      setShowTransactionForm(false)
    } catch (error) {
      console.error('SpendingPage: Failed to add transaction:', error)
      // Re-throw the error so the form can display it
      throw error
    }
  }

  const handleEditTransaction = async (updatedTransaction: Transaction | Omit<Transaction, 'id'>) => {
    try {
      if ('id' in updatedTransaction) {
        await updateTransactionInDB(updatedTransaction.id, updatedTransaction)
        setEditingTransaction(null)
      } else {
        // This is a new transaction, treat it as add
        await addTransactionToDB(updatedTransaction)
        setEditingTransaction(null)
      }
    } catch (error) {
      console.error('Failed to update transaction:', error)
      // You could add a toast notification here
    }
  }

  const handleDeleteTransaction = async (transactionId: string) => {
    try {
      await deleteTransactionFromDB(transactionId)
    } catch (error) {
      console.error('Failed to delete transaction:', error)
      // You could add a toast notification here
    }
  }

  const handleEditClick = (transaction: Transaction) => {
    setEditingTransaction(transaction)
    setShowTransactionForm(true)
  }

  const handleFormClose = () => {
    setShowTransactionForm(false)
    setEditingTransaction(null)
  }

  return (
    <div className="min-h-screen bg-background-950">
      {/* Navigation */}
      <Navigation />
      
      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-background-100 mb-2">
                Financial Management
              </h1>
              <p className="text-background-400">
                Track expenses, create budgets, and get AI-powered financial advice
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowTransactionForm(true)}
              className="luxury-button primary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Add Transaction</span>
            </motion.button>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-1 bg-background-800 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                activeTab === 'overview'
                  ? 'bg-accent-500 text-white shadow-lg'
                  : 'text-background-300 hover:text-background-100 hover:bg-background-700'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              <span>Overview</span>
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                activeTab === 'transactions'
                  ? 'bg-accent-500 text-white shadow-lg'
                  : 'text-background-300 hover:text-background-100 hover:bg-background-700'
              }`}
            >
              <Calculator className="w-4 h-4" />
              <span>Transactions</span>
            </button>
            <button
              onClick={() => setActiveTab('budget')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                activeTab === 'budget'
                  ? 'bg-accent-500 text-white shadow-lg'
                  : 'text-background-300 hover:text-background-100 hover:bg-background-700'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              <span>Budget</span>
            </button>
          </div>
        </motion.div>

        {/* Tab Content */}
        <div className="mb-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Overview and Budget */}
              <div className="lg:col-span-1 space-y-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <SpendingOverview transactions={transactions} />
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  {savedBudgets.length > 0 ? (
                    <div className="luxury-card p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-background-100">Active Budgets</h3>
                        <button
                          onClick={() => setActiveTab('budget')}
                          className="text-accent-400 hover:text-accent-300 text-sm transition-colors"
                        >
                          View All →
                        </button>
                      </div>
                      <div className="space-y-3">
                        {savedBudgets.filter(b => b.status === 'active').slice(0, 2).map((budget) => (
                          <div key={budget.id} className="p-4 bg-background-800 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium text-background-100">{budget.name}</h4>
                              <span className="text-sm text-background-400">
                                ${budget.monthly_income.toLocaleString()}/mo
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-green-400">Savings: </span>
                                <span className="text-background-200">${budget.savings_goal.toLocaleString()}</span>
                              </div>
                              <div>
                                <span className="text-purple-400">Investment: </span>
                                <span className="text-background-200">${budget.investment_goal.toLocaleString()}</span>
                              </div>
                            </div>
                            <div className="mt-2 text-xs text-background-400">
                              {Math.round(((budget.savings_goal + budget.investment_goal) / budget.monthly_income) * 100)}% savings rate
                            </div>
                          </div>
                        ))}
                        {savedBudgets.filter(b => b.status === 'active').length === 0 && (
                          <div className="text-center py-4">
                            <p className="text-background-400 text-sm mb-2">No active budgets</p>
                            <button
                              onClick={() => setActiveTab('budget')}
                              className="text-accent-400 hover:text-accent-300 text-sm transition-colors"
                            >
                              Create your first budget →
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="luxury-card p-6 text-center">
                      <div className="w-12 h-12 bg-accent-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <Sparkles className="w-6 h-6 text-accent-400" />
                      </div>
                      <h3 className="font-semibold text-background-100 mb-2">Create Your First Budget</h3>
                      <p className="text-background-400 text-sm mb-3">
                        Chat with Kubera to get personalized budget recommendations
                      </p>
                      <button
                        onClick={() => setActiveTab('budget')}
                        className="luxury-button primary text-sm px-4 py-2"
                      >
                        Start Budgeting
                      </button>
                    </div>
                  )}
                </motion.div>
              </div>

              {/* Right Column - Recent Transactions Preview */}
              <div className="lg:col-span-2">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="luxury-card p-6"
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-background-100">
                      Recent Transactions
                    </h3>
                    <button
                      onClick={() => setActiveTab('transactions')}
                      className="text-accent-400 hover:text-accent-300 text-sm transition-colors"
                    >
                      View All →
                    </button>
                  </div>
                  
                  {/* Show last 5 transactions */}
                  <div className="space-y-3">
                    {transactions.slice(0, 5).map((transaction) => (
                      <div key={transaction.id} className="flex items-center justify-between p-3 bg-background-800 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-background-700 rounded-lg flex items-center justify-center">
                            <span className="text-sm font-bold">{transaction.type === 'income' ? '↑' : '↓'}</span>
                          </div>
                          <div>
                            <p className="text-background-100 font-medium">{transaction.description}</p>
                            <p className="text-background-400 text-sm">{transaction.category}</p>
                          </div>
                        </div>
                        <div className={`font-semibold ${transaction.type === 'income' ? 'text-green-500' : 'text-red-500'}`}>
                          {transaction.type === 'income' ? '+' : '-'}${transaction.amount.toFixed(2)}
                        </div>
                      </div>
                    ))}
                    
                    {transactions.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-background-400 mb-4">No transactions yet</p>
                        <button
                          onClick={() => setShowTransactionForm(true)}
                          className="luxury-button primary"
                        >
                          Add Your First Transaction
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <TransactionList
                transactions={transactions}
                onEdit={handleEditClick}
                onDelete={handleDeleteTransaction}
              />
            </motion.div>
          )}

          {/* Budget Tab */}
          {activeTab === 'budget' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              {/* Saved Budgets Section */}
              <div className="luxury-card p-6">
                <BudgetList
                  budgets={savedBudgets}
                  loading={budgetsLoading}
                  error={budgetsError}
                  onDeleteBudget={deleteBudget}
                  onUpdateStatus={updateBudgetStatus}
                  onRefresh={refreshBudgets}
                />
              </div>

              {/* AI Budget Assistant Section */}
              <div>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-background-100 flex items-center space-x-2">
                    <Sparkles className="w-5 h-5 text-accent-400" />
                    <span>Create New Budget with Kubera</span>
                  </h3>
                  <p className="text-background-400 text-sm mt-1">
                    Chat with our AI assistant to create personalized budget recommendations
                  </p>
                </div>
              
              {/* Check if AI service is enabled and properly configured */}
              {import.meta.env.VITE_AI_BUDGET_ENABLED === 'true' && import.meta.env.VITE_GEMINI_API_KEY ? (
                <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
                  {/* AI Chat Interface */}
                  <div className="xl:col-span-3">
                    <div className="luxury-card p-0 overflow-hidden" style={{ height: '600px' }}>
                      <AIBudgetChat
                        session={currentSession}
                        messages={messages}
                        isLoading={aiLoading}
                        error={aiError}
                        onSendMessage={sendMessage}
                        onClearError={clearError}
                        onCreateNewSession={createNewSession}
                        latestBudgetRecommendation={latestBudgetRecommendation}
                        budgetMonthlyIncome={budgetMonthlyIncome}
                        onSaveBudget={async (budgetName: string, description?: string) => {
                          await saveBudget(budgetName, description)
                          // Refresh the budget list after saving
                          await refreshBudgets()
                        }}
                        className="h-full"
                      />
                    </div>
                  </div>

                  {/* AI Tips Sidebar */}
                  <div className="xl:col-span-1">
                    <div className="luxury-card p-6 space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-background-100 mb-4 flex items-center space-x-2">
                          <Sparkles className="w-5 h-5 text-accent-400" />
                          <span>AI Tips</span>
                        </h3>
                        <div className="space-y-4">
                          <div className="p-3 bg-background-800 rounded-lg">
                            <p className="text-background-300 text-sm mb-2">
                              <strong>Be specific:</strong> Share exact income and expense amounts for better recommendations.
                            </p>
                          </div>
                          <div className="p-3 bg-background-800 rounded-lg">
                            <p className="text-background-300 text-sm mb-2">
                              <strong>Set goals:</strong> Tell Kubera about your savings targets and investment interests.
                            </p>
                          </div>
                          <div className="p-3 bg-background-800 rounded-lg">
                            <p className="text-background-300 text-sm mb-2">
                              <strong>Use your data:</strong> Kubera can analyze your existing transactions for insights.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Quick Stats */}
                      {transactions.length > 0 && (
                        <div>
                          <h4 className="text-md font-semibold text-background-100 mb-3">Your Data</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-background-400">Transactions:</span>
                              <span className="text-background-200">{transactions.length}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-background-400">This Month:</span>
                              <span className="text-background-200">
                                {transactions.filter(t => {
                                  const transactionDate = new Date(t.date)
                                  const now = new Date()
                                  return transactionDate.getMonth() === now.getMonth() && 
                                         transactionDate.getFullYear() === now.getFullYear()
                                }).length}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="luxury-card p-8 text-center">
                  <div className="w-16 h-16 bg-accent-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-8 h-8 text-accent-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-background-100 mb-4">
                    Kubera AI Assistant Setup Required
                  </h3>
                  <p className="text-background-400 mb-6 max-w-md mx-auto">
                    To use the AI Budget feature, you need to configure your Gemini API key.
                  </p>
                  <div className="bg-background-800 rounded-lg p-4 text-left max-w-md mx-auto">
                    <h4 className="text-background-100 font-medium mb-2">Setup Steps:</h4>
                    <ol className="space-y-1 text-background-300 text-sm">
                      <li>1. Get API key from Google AI Studio</li>
                      <li>2. Add VITE_GEMINI_API_KEY to .env.local</li>
                      <li>3. Set VITE_AI_BUDGET_ENABLED=true</li>
                      <li>4. Restart your development server</li>
                    </ol>
                  </div>
                </div>
              )}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Transaction Form Modal */}
      {showTransactionForm && (
        <TransactionForm
          transaction={editingTransaction}
          onSubmit={editingTransaction ? handleEditTransaction : handleAddTransaction}
          onClose={handleFormClose}
        />
      )}
    </div>
  )
}

export default SpendingPage
